import React, { useEffect, useState, useRef } from "react";
import { Table, Tag, Popover, message, Modal, Select, DatePicker, Spin } from "antd";
import { FiPlus } from "react-icons/fi";
import { AiOutlineEye, AiOutlineDelete } from "react-icons/ai";
import { FiDownload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ReactDOM from "react-dom/client";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { LiaEditSolid } from "react-icons/lia";
import { Form } from "react-bootstrap";
import "./taxinvoice.css";
import Instance from "../../AxiosConfig";
import ProformaInvoicePage from "./ProformaInvoicePage";

const ProformaInvoiceTable = () => {
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const hiddenContainerRef = useRef(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [currency, setCurrency] = useState("All");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
const [downloadingId, setDownloadingId] = useState(null);

  // Debounce timer ref
  const debounceTimer = useRef(null);

  const statusOptions = [
    { label: "Active", value: "Active", color: "#faad14" },
    { label: "Closed", value: "Closed", color: "#52c41a" },
    { label: "Lost", value: "Lost", color: "#ff4d4f" },
  ];

  const handleStatusChange = async (id, newStatus) => {
    try {
      const response = await Instance.patch(`/proformaInvoices/status/${id}`, {
        status: newStatus,
      });

      if (response.status === 200 || response.status === 204) {
        setInvoiceData((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, status: newStatus } : item
          )
        );
        message.success("Status updated successfully!");
      } else {
        message.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Error updating status");
    }
  };

  // Fetch filtered data with debounce
  const fetchFilteredData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Build query parameters
      const params = new URLSearchParams();

      if (search.trim()) {
        params.append("search", search.trim());
      }

      if (status && status !== "All") {
        params.append("status", status);
      }

      if (currency && currency !== "All") {
        params.append("currency", currency);
      }

      if (fromDate) {
        params.append("startDate", fromDate);
      }

      if (toDate) {
        params.append("endDate", toDate);
      }

      const queryString = params.toString();
      const endpoint = queryString
        ? `/proformaInvoices/filter/search?${queryString}`
        : `/proformaInvoices`;

      const response = await Instance.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        const invoices = response?.data?.invoices || [];
        setInvoiceData(invoices);
      } else {
        message.error("Failed to fetch invoice data");
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error("Error fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  // Debounced filter function
  const debouncedFetch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchFilteredData();
    }, 500); // 500ms delay
  };

  // Initial load
  useEffect(() => {
    fetchFilteredData();
  }, []);

  // Listen to filter changes
  useEffect(() => {
    debouncedFetch();

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search, status, currency, fromDate, toDate]);

  const handleAddInvoice = () => {
    navigate("/add-proforma-invoice");
  };

  const handleDownloadPDF = async (record, termId) => {
    try {
      setDownloadingId(record._id);

      // ✅ ADD HERE (before rendering)
      document.body.classList.add("pdf-mode");
      const container = hiddenContainerRef.current;
      if (!container) return;

      const tempDiv = document.createElement("div");
      container.appendChild(tempDiv);

      const root = ReactDOM.createRoot(tempDiv);
      root.render(<ProformaInvoicePage id={record._id} termId={termId} />);

      await new Promise((resolve) => {
        const interval = setInterval(() => {
          const hasData =
            tempDiv.querySelector(".invoice-title") ||
            tempDiv.innerHTML.includes("PROFORMA INVOICE");
          if (hasData) {
            clearInterval(interval);
            setTimeout(resolve, 800);
          }
        }, 300);
      });

      const canvas = await html2canvas(tempDiv, {
        scale: 1,
        useCORS: true,
        allowTaint: true,
      });
    const imgData = canvas.toDataURL("image/jpeg", 1);
   
         const pdf = new jsPDF("p", "mm", "a4");
   
         // ✅ SIMPLE (no scaling issues now)
         pdf.addImage(imgData, "JPEG", 0, 0, 210, 297);
         const fileName = termId
           ? `${record.invoiceNo}-${termId.slice(-6)}.pdf`
           : `${record.invoiceNo}.pdf`;
   
         pdf.save(fileName);

      root.unmount();
      container.removeChild(tempDiv);

      message.success("Invoice PDF downloaded successfully!");
    } catch (err) {
      console.error(err);
      message.error("Failed to generate invoice PDF.");
    }
    finally{
      setDownloadingId(null); 
      document.body.classList.remove("pdf-mode");
    }
  };

  const handleDeleteInvoice = async (id) => {
    Modal.confirm({
      title: (
        <span style={{ fontWeight: 600, color: "#000", fontSize: "16px" }}>
          Delete this invoice?
        </span>
      ),
      icon: (
        <ExclamationCircleOutlined
          style={{ color: "#ff4d4f", fontSize: "20px" }}
        />
      ),
      content: (
        <span style={{ color: "#555", fontSize: "14px" }}>
          This action cannot be undone. Are you sure you want to proceed?
        </span>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      centered: true,
      maskClosable: true,
      closable: true,
      okButtonProps: {
        style: {
          backgroundColor: "#ff4d4f",
          borderColor: "#ff4d4f",
          fontWeight: 500,
          color: "white",
        },
      },
      cancelButtonProps: { style: { borderRadius: 6, fontWeight: 500 } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await Instance.delete(`/proformaInvoices/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.status === 200 || response.status === 204) {
            setInvoiceData((prev) =>
              prev.filter((invoice) => invoice._id !== id)
            );
            message.success("Invoice deleted successfully!");
          }
        } catch (error) {
          console.error("Error deleting invoice:", error);
          const errorMsg =
            error.response?.data?.message || "Failed to delete invoice";
          message.error(errorMsg);
        }
      },
    });
  };

  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      fixed: "left",
      render: (val) => <strong>{val}</strong>,
      sorter(a, b) {
        return a.invoiceNo.localeCompare(b.invoiceNo);
      },
      defaultSortOrder: 'descend',
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Select
          value={status}
          onChange={(newStatus) => handleStatusChange(record._id, newStatus)}
          style={{
            width: 120,
            fontWeight: 500,
          }}
          dropdownStyle={{ borderRadius: 8 }}
        >
          {statusOptions.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              <Tag
                color={
                  opt.value === "Active"
                    ? "gold"
                    : opt.value === "Closed"
                    ? "green"
                    : "red"
                }
                style={{ fontWeight: 500 }}
              >
                {opt.label}
              </Tag>
            </Select.Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Standard",
      dataIndex: "standard",
      render: (_, record) =>
        Array.isArray(record?.standard)
          ? record.standard.map((s) => s.standardName).join(", ")
          : "—",
    },
    {
      title: "Agent",
      key: "agentName",
      render: (_, record) => record?.agentId?.agentName || "—",
    },
    {
      title: "Certification Type",
      dataIndex: "certificationType",
      maxwidth: 150,
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (currency) => (
        <Tag
          color={currency === "INR" ? "green" : "gold"}
          style={{ fontWeight: 500 }}
        >
          {currency}
        </Tag>
      ),
    },
    {
      title: "Total Closure",
      dataIndex: "baseClosureAmount",
      key: "baseClosureAmount",
      render: (val, record) =>
        `${record.currency} ${(val || 0).toLocaleString()}`,
    },
    {
      title: "Term Name",
      render: (_, record) => {
        return `${record?.terms?.[0]?.termName || "—"}`;
      },
    },
    {
      title: "Term Amount",
      render: (_, record) => {
        const firstTerm = record?.terms?.[0];
        const amount = firstTerm?.baseAmount ?? "—";
        const currency = record?.currency || "";

        return `${amount} ${currency}`.trim();
      },
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (val) => dayjs(val).format("DD MMM YYYY"),
    },
    {
      title: "Action",
      key: "action",
      width: 180,
      render: (_, record) => {
        if (!record) {
          return <span style={{ color: "#aaa" }}>—</span>;
        }

        return {
          children: (
            <div className="d-flex gap-3 justify-content-start">
              <Popover content="View Invoice">
                <AiOutlineEye
                  size={18}
                  style={{ cursor: "pointer", color: "#1890ff" }}
                  onClick={() =>
                    navigate(`/proforma-invoice-page/${record._id}`)
                  }
                />
              </Popover>

              <Popover content="Download PDF">
                 {downloadingId === record._id ? (
                    <Spin size="small" />
                  ) : (
                    <FiDownload
                      size={18}
                      style={{ cursor: "pointer", color: "#52c41a" }}
                      onClick={() => handleDownloadPDF(record, null)}
                    />
                  )}
              </Popover>

              <Popover content="Delete Invoice">
                <AiOutlineDelete
                  size={18}
                  style={{ cursor: "pointer", color: "#ff4d4f" }}
                  onClick={() => handleDeleteInvoice(record._id)}
                />
              </Popover>

              <Popover content="Edit Invoice">
                <LiaEditSolid
                  size={18}
                  style={{ cursor: "pointer", color: "#f39c12" }}
                  onClick={() =>
                    navigate(`/edit-proforma-invoice/${record._id}`)
                  }
                />
              </Popover>
            </div>
          ),
        };
      },
    },
  ];

  const handleClearFilters = () => {
    setSearch("");
    setStatus("All");
    setCurrency("All");
    setFromDate(null);
    setToDate(null);
  };

  return (
    <>
      {/* Filter Section */}
      <div className="filter-card p-3">
        <Form>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by company or invoice no..."
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option>All</option>
                  <option>Active</option>
                  <option>Closed</option>
                  <option>Lost</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option>All</option>
                  <option>INR</option>
                  <option>USD</option>
                  <option>EUR</option>
                  <option>GBP</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={(date) =>
                    setFromDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={toDate ? dayjs(toDate) : null}
                  onChange={(date) =>
                    setToDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="clear-btn-company w-100"
                onClick={handleClearFilters}
                style={{ height: "38px" }}
              >
                Clear
              </button>
            </div>
          </div>
        </Form>

        {(search ||
          status !== "All" ||
          currency !== "All" ||
          fromDate ||
          toDate) && (
          <div className="mt-3">
            <small className="text-muted">
              {loading
                ? "Loading filtered results..."
                : `Showing ${invoiceData.length} result(s)`}
            </small>
          </div>
        )}
      </div>

      <div className="taxinvoice-wrapper mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="proforma-title">Proforma Invoices</h2>
          <button
            className="add-certification-btn"
            onClick={handleAddInvoice}
            style={{
              backgroundColor: "#1890ff",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "6px 16px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FiPlus /> Add New Invoice
          </button>
        </div>

        <Table
          columns={columns}
          dataSource={invoiceData}
          loading={loading}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: "max-content" }}
          bordered
          rowKey={(record) => record._id}
        />

        <div
          ref={hiddenContainerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      </div>
    </>
  );
};

export default ProformaInvoiceTable;
